import { z } from 'zod';
import type { QualitySkillExecution } from '@/lib/api/quality-types.js';

/** Validates optional report evidence before the renderer traverses its nested values. */
const skillExecutionSchema: z.ZodType<QualitySkillExecution> = z.object({
  version: z.literal(1),
  invocations: z.array(
    z.object({
      activation_id: z.string(),
      skill_id: z.string(),
      trigger: z.string().optional(),
      title: z.string().optional(),
      scope: z.string().optional(),
      digest: z.string(),
      phase: z.enum(['step', 'precondition']),
      index: z.number().int().nonnegative(),
      status: z.string(),
      outputs: z.record(z.unknown()),
      actions: z.array(
        z.object({
          action: z.string(),
          status: z.string(),
          observation: z.string().optional(),
          expected_text: z.string().optional(),
          actual_text: z.string().optional(),
          receipts: z.array(z.object({ completed: z.boolean() })),
        }),
      ),
    }),
  ),
});

/** A loaded procedure remains visibly distinct from completed execution. */
export function SkillExecutionDetails({ evidence }: { evidence?: unknown }) {
  const validated = skillExecutionSchema.safeParse(evidence);
  if (!validated.success || !validated.data.invocations.length) return null;
  return (
    <div
      className="mt-2 space-y-1 text-xs"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {validated.data.invocations.map((invocation) => (
        <details key={invocation.activation_id} className="rounded border p-2">
          <summary className="cursor-pointer">
            Skill /{invocation.trigger ?? invocation.skill_id} ·{' '}
            {invocation.status === 'passed'
              ? 'Executed'
              : invocation.status === 'loaded'
                ? 'Loaded; execution incomplete'
                : invocation.status}
          </summary>
          <p className="mt-1 text-muted-foreground">
            {invocation.title ?? invocation.skill_id} ·{' '}
            {invocation.scope ?? 'frozen snapshot'} · Revision{' '}
            {invocation.digest.slice(0, 12)} · {invocation.phase}{' '}
            {invocation.index + 1}
          </p>
          <ol className="mt-2 list-decimal space-y-2 pl-5">
            {invocation.actions.map((action, index) => (
              <li key={index}>
                <p>
                  {action.action} · {action.status}
                </p>
                <p className="text-muted-foreground">
                  {action.observation || 'No observation recorded'}
                </p>
                {action.expected_text !== undefined ||
                action.actual_text !== undefined ? (
                  <p>
                    Expected: {action.expected_text ?? 'Not recorded'} · Actual:{' '}
                    {action.actual_text ?? 'Not recorded'}
                  </p>
                ) : null}
                <p>
                  {
                    action.receipts.filter((receipt) => receipt.completed)
                      .length
                  }{' '}
                  completed tool receipt(s)
                </p>
              </li>
            ))}
          </ol>
          {Object.keys(invocation.outputs).length ? (
            <pre className="mt-2 overflow-x-auto whitespace-pre-wrap">
              {JSON.stringify(invocation.outputs, null, 2)}
            </pre>
          ) : null}
        </details>
      ))}
    </div>
  );
}
